/**
 * Something to tap.
 *
 * A till with only a search box works fine when a scanner is in your hand and
 * everything has a label on it. It is useless when somebody asks for the blue
 * linen shirt and you have to guess how it is spelled.
 *
 * So: the shop, laid out. Categories along the top, products as cards, tap one
 * and it is on the sale. Scanning and searching still work and are still faster
 * - this is the way in for everything else.
 */
import { useEffect, useMemo, useState } from 'react';
import { compareSizes, inScope, promoBadge } from '@pos/shared';
import type { CatalogItem } from '@pos/shared';
import { useI18n, useT } from '../../core/i18n/index.js';
import { useSettings } from '../../core/state/settings.js';
import { useSync } from '../../core/sync.js';
import { db } from '../../core/db.js';
import { Button, Empty, Loading, Modal, Pill, Swatch } from '../../core/ui/ui.js';
import { Photo } from '../../core/ui/bits.js';
import { useRunningPromotions } from '../../core/promotions.js';

/** One style, with all its sizes and colours gathered up. */
interface Style {
  productId: string;
  nameEn: string;
  nameAr: string;
  price: number;
  categoryId: string | null;
  categoryEn: string;
  categoryAr: string;
  variants: CatalogItem[];
  stock: number;
  /** Priced at the till: no price to show, and nothing to be sold out of. */
  openPrice: boolean;
  colours: { hex: string; en: string; ar: string }[];
  imageUrl: string | null;
  brandId: string | null;
}

function group(items: CatalogItem[]): Style[] {
  const byProduct = new Map<string, Style>();
  for (const v of items) {
    let s = byProduct.get(v.productId);
    if (!s) {
      s = {
        productId: v.productId,
        nameEn: v.nameEn,
        nameAr: v.nameAr,
        price: v.price,
        categoryId: v.categoryId ?? null,
        categoryEn: v.categoryEn ?? '',
        categoryAr: v.categoryAr ?? '',
        variants: [],
        stock: 0,
        openPrice: !!v.openPrice,
        colours: [],
        imageUrl: v.imageUrl ?? null,
        brandId: v.brandId ?? null,
      };
      byProduct.set(v.productId, s);
    }
    s.variants.push(v);
    s.stock += Math.max(0, v.stock);
    if (v.colorHex && !s.colours.some((c) => c.hex === v.colorHex)) {
      s.colours.push({ hex: v.colorHex, en: v.colorEn, ar: v.colorAr });
    }
  }
  return [...byProduct.values()];
}

export function ProductGrid({ onPick }: { onPick: (item: CatalogItem) => void }) {
  const t = useT();
  const { lang } = useI18n();
  const { money } = useSettings();
  const { snapshotAt, state } = useSync();
  const [styles, setStyles] = useState<Style[] | null>(null);
  const [category, setCategory] = useState<string>('all');
  const [choosing, setChoosing] = useState<Style | null>(null);
  const promos = useRunningPromotions();
  /** The offer a tile is part of - the biggest percent, or the first buy-X-get-Y. */
  const offerFor = (s: Style) => {
    const mine = promos.filter((p) => inScope(p, { productId: s.productId, categoryId: s.categoryId, brandId: s.brandId }));
    if (!mine.length || s.openPrice) return null;
    return mine.sort((a, b) => (b.kind === 'percent' ? b.percent : 0) - (a.kind === 'percent' ? a.percent : 0))[0];
  };

  // Re-read whenever a fresh product list lands on the device. On a first
  // sign-in that is the moment the tiles appear at all; later it is how stock
  // counts on the tiles keep up with the five-minute refresh.
  useEffect(() => {
    let alive = true;
    void db.items.toArray().then((rows) => {
      // A price of zero means nobody has priced it yet, and an unpriced tile is
      // a trap. An open-price item is the exception: having no price of its own
      // is what it is for, and it is the one tile a cashier reaches for most.
      if (alive) setStyles(group(rows.filter((r) => r.price > 0 || r.openPrice)));
    });
    return () => {
      alive = false;
    };
  }, [snapshotAt]);

  const categories = useMemo(() => {
    if (!styles) return [];
    const seen = new Map<string, { id: string; en: string; ar: string; n: number }>();
    for (const s of styles) {
      const id = s.categoryId ?? 'none';
      const found = seen.get(id);
      if (found) found.n += 1;
      else seen.set(id, { id, en: s.categoryEn || '—', ar: s.categoryAr || '—', n: 1 });
    }
    return [...seen.values()].sort((a, b) => b.n - a.n);
  }, [styles]);

  const shown = useMemo(() => {
    if (!styles) return [];
    const list = category === 'all' ? styles : styles.filter((s) => (s.categoryId ?? 'none') === category);
    return [...list].sort((a, b) => (lang === 'ar' ? a.nameAr : a.nameEn).localeCompare(lang === 'ar' ? b.nameAr : b.nameEn));
  }, [styles, category, lang]);

  if (!styles) return <Loading />;
  if (styles.length === 0) {
    // Nothing on the device yet and the first download is still on its way -
    // that is not "no products", it is "not here yet".
    if (!snapshotAt && state !== 'offline') return <Loading label={t('products.firstLoad')} />;
    return <Empty icon="👕" title={t('products.empty')} hint={t('products.emptyHint')} />;
  }

  const nm = (s: { nameEn: string; nameAr: string }) =>
    lang === 'ar' ? s.nameAr || s.nameEn : s.nameEn || s.nameAr;

  /** One size and one colour means there is nothing to ask. */
  const pick = (s: Style) => {
    // Nothing is in stock of an open-price item, and that is not a reason to
    // make the cashier choose between its one and only square.
    if (s.openPrice) {
      onPick(s.variants[0]!);
      return;
    }
    const inStock = s.variants.filter((v) => v.stock > 0);
    if (s.variants.length === 1) onPick(s.variants[0]);
    else if (inStock.length === 1) onPick(inStock[0]);
    else setChoosing(s);
  };

  return (
    <div className="grid-browse">
      <div className="cat-bar">
        <button className={`cat ${category === 'all' ? 'on' : ''}`} onClick={() => setCategory('all')}>
          {t('common.all')}
        </button>
        {categories.map((c) => (
          <button key={c.id} className={`cat ${category === c.id ? 'on' : ''}`} onClick={() => setCategory(c.id)}>
            {lang === 'ar' ? c.ar : c.en}
          </button>
        ))}
      </div>

      <div className="tiles">
        {shown.map((s) => (
          <button key={s.productId} className={`tile ${s.stock === 0 && !s.openPrice ? 'gone' : ''}`} onClick={() => pick(s)}>
            <Photo url={s.imageUrl} small className="tile-photo" />
            {offerFor(s) ? <span className="tile-offer">{promoBadge(offerFor(s)!)}</span> : null}
            <span className="tile-name">{nm(s)}</span>
            <span className="tile-colours">
              {s.colours.slice(0, 6).map((c) => (
                <span key={c.hex} className="swatch" style={{ background: c.hex }} />
              ))}
            </span>
            <span className="tile-foot">
              {/* It has no price of its own and it is never sold out; saying
                  "0.00, sold out" about the shop's catch-all is just wrong. */}
              {s.openPrice ? (
                <span className="tile-stock">{t('catalog.openPriceTile')}</span>
              ) : (
                <>
                  <span className="tile-price tnum">{money(s.price)}</span>
                  {s.stock === 0 ? (
                    <span className="tile-stock out">{t('priceCheck.soldOut')}</span>
                  ) : (
                    <span className="tile-stock tnum">{t('till.left', { n: s.stock })}</span>
                  )}
                </>
              )}
            </span>
          </button>
        ))}
      </div>

      {choosing ? (
        <VariantPicker
          style={choosing}
          onPick={(v) => {
            onPick(v);
            setChoosing(null);
          }}
          onClose={() => setChoosing(null)}
        />
      ) : null}
    </div>
  );
}

/** Which size, and which colour. Nothing that is sold out is offered. */
function VariantPicker({
  style,
  onPick,
  onClose,
}: {
  style: Style;
  onPick: (v: CatalogItem) => void;
  onClose: () => void;
}) {
  const t = useT();
  const { lang } = useI18n();
  const { money } = useSettings();

  const colours = style.colours.filter((c) =>
    style.variants.some((v) => v.colorHex === c.hex && v.stock > 0),
  );
  const [colour, setColour] = useState<string | null>(colours[0]?.hex ?? null);

  const sizes = style.variants
    .filter((v) => (colour ? v.colorHex === colour : true))
    .sort((a, b) => compareSizes(a.size, b.size) || a.colorEn.localeCompare(b.colorEn));

  const nm = lang === 'ar' ? style.nameAr || style.nameEn : style.nameEn || style.nameAr;

  return (
    <Modal title={nm} onClose={onClose} footer={<Button onClick={onClose}>{t('common.cancel')}</Button>}>
      <Photo url={style.imageUrl} className="picker-photo" />
      {colours.length > 1 ? (
        <>
          <div className="picker-label">{t('common.colour')}</div>
          <div className="btn-row">
            {colours.map((c) => (
              <Button
                key={c.hex}
                variant={colour === c.hex ? 'primary' : 'default'}
                onClick={() => setColour(c.hex)}
              >
                <Swatch hex={c.hex} /> {lang === 'ar' ? c.ar || c.en : c.en || c.ar}
              </Button>
            ))}
          </div>
        </>
      ) : null}

      <div className="picker-label" style={{ marginBlockStart: colours.length > 1 ? 'var(--s-5)' : 0 }}>
        {t('common.size')}
      </div>
      <div className="size-pick">
        {sizes.map((v) => (
          <button
            key={v.variantId}
            className={`size-btn ${v.stock <= 0 ? 'gone' : ''}`}
            disabled={v.stock <= 0}
            onClick={() => onPick(v)}
          >
            <span className="s">{v.size}</span>
            <span className="n tnum">{v.stock > 0 ? v.stock : t('priceCheck.soldOut')}</span>
          </button>
        ))}
      </div>

      <div className="row" style={{ marginBlockStart: 'var(--s-5)' }}>
        <Pill kind="accent">{money(style.price)}</Pill>
        <div className="spacer" />
        <span className="xsmall muted">{t('priceCheck.otherSizes')}</span>
      </div>
    </Modal>
  );
}
